import Calendar, { CalendarType, GuestCollectionMode, RecurringAppointmentsPaymentType, TeamMember } from '@/models/calendar'
import { IConnection, IUserCalendar } from '@/types/connection'
import { computed, reactive } from 'vue'

import { CalendarGroupService } from '@/class/services/CalendarGroupService'
import { CalendarService } from '@/class/services/CalendarService'
import { CustomFieldService } from '@/class/services/CustomFieldService'
import { LocationService } from '@/class/services/LocationService'
import { TemplateService } from '@/class/services/TemplateService'
import { UserService } from '@/class/services/UserService'
import Contact from '@/models/contact'
import Location from '@/models/location'
import User from '@/models/user'
import { sortOptions } from '@/utils/generalFunctions'
import { saveLocale } from '@/utils/locale'
import moment from 'moment'
import Postmate from 'postmate'
import { CalendarLocationSettingsService } from '../class/services/CalendarLocationSettingsService'
import { CalendarResourceService } from '../class/services/CalendarResourceService'
import { CalendarServiceMenuService } from '../class/services/CalendarServiceMenuService'
import { CalendarUserSettings } from '../class/services/CalendarUserSettings'
import { ConnectionService } from '../class/services/ConnectionService'
import { CustomValueService } from '../class/services/CustomValueService'
import { formService } from '../class/services/FormService'
import { WhatsappTemplateService } from '../class/services/WhatsappService'
import CalendarLocationSettings from '../models/calendar_location_settings'
import { CALENDAR_RESOURCE_TYPES } from '../models/calendar_resource'
import ServiceMenu from '../models/service_menu'
import { CalendarScopes } from '../types'

declare global {
  interface Window {
    pendo: any
  }
}

const AppState = reactive({
  parentApp: null as Postmate.Model | null,
  filteredContacts: [] as Contact[],
  modalStep: 'select-contact',
  isConnectionSyncLoading: false,
  selectedContact: undefined as Contact | undefined,
  apiKey: '' as string,
  isUserPartOfLocation: true as boolean,
  isRecurringEnabled: false as boolean,
  disableContactTimezone: false as boolean,
  baseUrl: '' as string,
  embedUrl: '' as string,
  authApiKey: '' as string,
  tokenId: '' as string,
  locationId: '' as string,
  source: '' as string,
  whitelabelDomain: '' as string,
  user: undefined as IUser | undefined,
  companyId: '',
  shouldAllowCustomTime: false,
  locationTimezone: 'UTC',
  locale: '',
  depth: 0,
  globalTeams: [],
  globalRooms: [],
  globalEquipments: [],
  globalCalendars: { calendars: [] } as { calendars: any[] },
  hasServiceCalendars: false,
  globalUsers: { users: [] } as { users: any[] },
  forms: [] as any,
  globalServiceMenus: [] as any,
  isLoading: false,
  loggedInUserDetail: null as User | null,
  currentLocationDetail: null as Location | null,
  calendarLocationDetails: null as CalendarLocationSettings | null,
  connections: {} as any,
  userCalendars: [] as IUserCalendar[],
  loadingAssets: {
    calendars: false,
    groups: false,
    users: false,
    editingCalendar: false,
    rooms: false,
    equipments: false,
    serviceMenus: false,
    editingServiceMenu: false,
  },
  editingCalendarId: '',
  editingCalendar: null as Calendar | null,
  draftCalendarData: null as any, // Temporary storage for calendar data before creation
  editingServiceMenu: null as ServiceMenu | null,
  isAdvancedCalendarPageLoading: false,
  shouldAllowPayment: false,
  paymentProviderInfo: null as any,
  defaultPaymentProvider: null as any,
  isPayPalEnabled: false,
  errorInDurationPickerMap: {
    slotInterval: false,
    slotDuration: false,
    slotBuffer: false,
    preBuffer: false,
    allowBookingAfter: false,
  },
  editCalendarValidationData: {
    haveMerchant: false,
    enableRecurring: false,
    enableChargeGuests: false,
    enableGuests: false,
    enableMandatoryGuestType: false,
    guestCollectionMode: GuestCollectionMode.EMAIL as GuestCollectionMode,
    teamMembers: [] as TeamMember[],
    selectedTeamMember: [] as any,
    recurringPaymentType: '' as any,
    enableSameUserAssignment: false,
    disableFormFirst: false,
    removeMultipleLocation: false,
    shouldAcceptPayment: false,
  },
  changesSaved: false,
  userPreferenceTimezone: 'UTC',
  merchant: {} as any,
  shouldAcceptPayment: false,
  isFormFirstEnabled: false,
  isClassicWidgetSelected: false,
  isMultipleLocationEnabled: false,
  isServicesEnabled: false,
  isCouponEnabled: false,
  mediaFileURI: '',
  isMediaLibraryOpen: false,
  mediaLibraryFieldType: '', // Track which field the media library is for
  clonedEmailTemplateId: '',
  emailTemplates: undefined as any,
  smsTemplates: undefined as any,
  whatsappTemplates: undefined as any,
  whatsappDefaultTemplates: undefined as any,
  customFields: undefined as any,
  standardFields: undefined as any,
  opportunityCustomFields: undefined as any,
  customValues: [] as any[],
  selectedSettingsTab: 'calendars',
  nestedCustomValues: [] as any[],
  showAdvancedCalendarSettings: false,
  showAdvancedServiceMenuSettings: false,
  selectedCalendarId: '',
  isCalendarsConnectionLoaded: false,
  isMerchantsLoaded: false,
  externalCalendars: {} as any,
  isCustomValuesLoaded: false,
  selectedServiceMenuId: '',
  selectedTab: 'calendars',
  selectedCalendarSetupStep: 'meeting-details' as string,
  isNewCalendar: false,
  isPartialPaymentChecked: false,
  showGroupSuccessAlert: false,
  showCalendarsMovedSuccessAlert: false,
  showSelectCalendarsModal: false,
  createdGroup: undefined as any,
  calendarsOnboarding: [] as any[],
  onboardingError: false,
  showCalendarLinkModal: false,
  isOnboardingLoading: false,
  exitTour: false,
  hasLocationChanged: false,
  customObjectLabels: null as any,
  showTextSnippetModal: false,
  fetchSmsTemplates: false,
  editingTemplateData: null as any,
  isScheduleEnabled: false,
  isUserAvailabilityUpdated: false,
  whatsappNotificationTemplate: {
    isUpdated: false,
    templateId: '',
  },
  isSmsNotificationsEnabled: false,
  isWhatsappNotificationsEnabled: false,
  calendarSubAccountConfig: null as any,
  queryParams: {} as any,
})

export type Option = {
  title: string
  description?: string
  value: string
  enableForHLOnly?: boolean
  isDisabled?: boolean
}

export type MasterOption = {
  title: string
  description?: string
  options?: Option[]
}

export interface IUser {
  id: string
  first_name?: string
  last_name?: string
  profile_photo?: string
  phone?: string
  email?: string
  type: string
  role: string
  permission?: any
  date_update?: string
  version?: number
}

export function initPendo() {
  if (AppState.companyId && AppState.user) {
    const initOptions = {
      visitor: {
        id: AppState.user.id,
      },
      account: {
        id: `${AppState.companyId}::${AppState.locationId}`,
      },
    }
    window.pendo && window.pendo.initialize(initOptions)
  }
}

export const trackPendoEvent = (eventName: string, eventProperties: { [key: string]: string }) => {
  if (window.pendo) {
    window.pendo.track(eventName, eventProperties)
  }
}

export const setParentApp = (app: Postmate.Model) => {
  AppState.parentApp = app
}

export function setApiKey(key: string) {
  AppState.apiKey = key
}

export function setIsUserPartOfLocation(key: boolean) {
  AppState.isUserPartOfLocation = key
}

export function setIsRecurringEnabled(key: boolean) {
  AppState.isRecurringEnabled = key
}

export function setIsServicesEnabled(isServicesEnabled: boolean) {
  AppState.isServicesEnabled = isServicesEnabled
}

export function setUserPreferenceTimezone(timezone: string) {
  AppState.userPreferenceTimezone = timezone
}

export function setIsCouponEnabled(isCouponEnabled: boolean) {
  AppState.isCouponEnabled = isCouponEnabled
}
export function setIsScheduleEnabled(isScheduleEnabled: boolean) {
  AppState.isScheduleEnabled = isScheduleEnabled
}

export function setDisableContactTImezone(disableContactTimezone: boolean) {
  AppState.disableContactTimezone = disableContactTimezone
}

export function setBaseUrl(baseUrl: string) {
  AppState.baseUrl = baseUrl
}

export function setEmbedUrl(embedUrl: string) {
  AppState.embedUrl = embedUrl
}

export function setAuthApiKey(key: string) {
  AppState.authApiKey = key
}

export function setCompanyId(id: string) {
  AppState.companyId = id
}

export function setConnections(connections: IConnection) {
  AppState.connections = connections
}

export function setUserCalendars(userCalendars: IUserCalendar[]) {
  AppState.userCalendars = userCalendars
}

export function setTokenId(tokenId: string) {
  AppState.tokenId = tokenId
}

export function setMediaFileURI(uri: string) {
  if (AppState.isMediaLibraryOpen) {
    AppState.mediaFileURI = uri
    AppState.isMediaLibraryOpen = false
  }
}

export function setMediaLibraryFieldType(fieldType: string) {
  AppState.mediaLibraryFieldType = fieldType
}

export function setLocationId(locationId: string) {
  if (locationId !== AppState.locationId) {
    AppState.hasLocationChanged = true
  }
  AppState.locationId = locationId
  clearGlobalCache()
}

export function setSource(source: string) {
  // console.log('source', source)
  AppState.source = source
}

export function setWhitelabelDomain(whitelabelDomain: string) {
  AppState.whitelabelDomain = whitelabelDomain
}

export function setLocationTimezone(locationTimezone: string) {
  AppState.locationTimezone = locationTimezone
}
// const { t, locale: globalLocale } = useI18n({ useScope: 'global' })

const momentSupportedLocales = ['en_US', 'fr', 'it', 'pt', 'es']

export function setLocale(locale: string) {
  AppState.locale = locale

  if (momentSupportedLocales.includes(locale)) {
    moment.locale(locale)
  } else {
    moment.locale('en')
  }

  saveLocale(locale)
  // globalLocale.value = locale
}

export const setFilteredContacts = (contacts: any[]) => {
  AppState.filteredContacts = contacts
}

export const setGlobalTeams = (teams: any) => {
  AppState.globalTeams = teams
}

export const setGlobalRooms = (rooms: any) => {
  AppState.globalRooms = rooms
}
export const setGlobalEquipments = (equipments: any) => {
  AppState.globalEquipments = equipments
}

export const setLoggedInUserDetail = (loggedInUserDetail: User) => {
  AppState.loggedInUserDetail = loggedInUserDetail
}

export const setCurrentLocationDetail = (currentLocationDetail: Location) => {
  AppState.currentLocationDetail = currentLocationDetail
}

export const setCalendarLocationDetail = (calendarLocationDetails: CalendarLocationSettings) => {
  AppState.calendarLocationDetails = calendarLocationDetails
}

const hasServiceCalendars = (calendars?: any[]) => {
  return Boolean(
    calendars?.some(
      calendar =>
        calendar.type === CalendarType.SERVICE ||
        calendar.calendarType === CalendarType.SERVICE
    )
  )
}

export const setGlobalCalendars = (calendars: any) => {
  AppState.globalCalendars = calendars
  AppState.hasServiceCalendars = hasServiceCalendars(calendars?.calendars)
}

export const setGlobalUsers = (users: any) => {
  AppState.globalUsers = users
}

export const setGlobalServiceMenus = (serviceMenus: any) => {
  AppState.globalServiceMenus = serviceMenus
}

export const setEditingCalendarId = (calendarId: string) => {
  AppState.editingCalendarId = calendarId
}

export const setEditingCalendar = (calendar: Calendar) => {
  AppState.editingCalendar = calendar
}

export const setEditingServiceMenu = (serviceMenu: any) => {
  AppState.editingServiceMenu = serviceMenu
}

export const setIsAdvancedCalendarPageLoading = (isLoading: boolean) => {
  AppState.isAdvancedCalendarPageLoading = isLoading
}

export const setShouldAllowPayment = (shouldAllowPayment: boolean) => {
  AppState.shouldAllowPayment = shouldAllowPayment
}

export const setQueryParams = (queryParams: any) => {
  AppState.queryParams = queryParams
}

export const setErrorInDurationPicker = (errorKey: string, hasError: boolean) => {
  AppState.errorInDurationPickerMap[errorKey] = hasError
}

export const setEditCalendarValidationData = (key: string, value: any) => {
  AppState.editCalendarValidationData[key] = value
}

export const setCalendarSubAccountConfig = (calendarSubAccountConfig: any) => {
  AppState.calendarSubAccountConfig = calendarSubAccountConfig
}

export const resetCalendarEditValidationData = () => {
  AppState.editCalendarValidationData = {
    haveMerchant: false,
    enableRecurring: false,
    enableChargeGuests: false,
    enableGuests: false,
    guestCollectionMode: GuestCollectionMode.EMAIL,
    teamMembers: [],
    selectedTeamMember: [] as any,
    recurringPaymentType: '' as any,
    enableSameUserAssignment: false,
    disableFormFirst: false,
    enableMandatoryGuestType: false,
    removeMultipleLocation: false,
    shouldAcceptPayment: false,
  }
  AppState.isFormFirstEnabled = false
  AppState.isClassicWidgetSelected = false
  AppState.selectedCalendarSetupStep = 'meeting-details'
}

export function setUser(user: IUser | undefined) {
  AppState.user = {
    id: user?.id || '',
    type: user?.type || '',
    role: user?.role || '',
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    profile_photo: user?.profile_photo || '',
    phone: user?.phone || '',
    permission: user?.permission,
    date_update: user?.date_update || '',
    version: user?.version,
  }
}

export const setIsLoading = (isLoading: boolean) => {
  AppState.isLoading = isLoading
}

export const setSelectedContact = (contact: Contact) => {
  AppState.selectedContact = contact
}

export const setCustomObjectLabels = (customObjectLabels: any) => {
  AppState.customObjectLabels = customObjectLabels
}

export const setIsSmsNotificationsEnabled = (isSmsNotificationsEnabled: boolean) => {
  AppState.isSmsNotificationsEnabled = isSmsNotificationsEnabled
}

export const setIsWhatsappNotificationsEnabled = (isWhatsappNotificationsEnabled: boolean) => {
  AppState.isWhatsappNotificationsEnabled = isWhatsappNotificationsEnabled
}

export const toggleChangesSaved = () => {
  AppState.changesSaved = !AppState.changesSaved
}

const USER_DETAILS_CACHE_TTL_MS = 5 * 60 * 1000
const CALENDAR_USER_SETTINGS_CACHE_TTL_MS = 5 * 60 * 1000
let userDetailsRequestId = 0
let usersLoadedForLocation: string | null = null
let teamsLoadedForLocation: string | null = null
let calendarsLoadedForRequestKey: string | null = null

const usersInLocationRequests = new Map<string, Promise<any>>()
const teamsInLocationRequests = new Map<string, Promise<any>>()
const calendarsInLocationRequests = new Map<string, Promise<any>>()
const userDetailsCache = new Map<
  string,
  {
    fetchedAt: number
    user: User
  }
>()
const userDetailsRequests = new Map<string, Promise<User>>()
const latestUserDetailsRequestIds = new Map<string, number>()
const calendarUserSettingsCache = new Map<
  string,
  {
    fetchedAt: number
    response: any
  }
>()
const calendarUserSettingsRequests = new Map<string, Promise<any>>()

export const invalidateUserDetailsCache = (userId?: string) => {
  if (userId) {
    userDetailsCache.delete(userId)
    return
  }

  userDetailsCache.clear()
}

export const invalidateCalendarUserSettingsCache = (
  locationId?: string,
  userId?: string
) => {
  if (locationId && userId) {
    calendarUserSettingsCache.delete(`${locationId}:${userId}`)
    return
  }

  calendarUserSettingsCache.clear()
}

export const getUsersInLocation = async (fetchData = false) => {
  if (!AppState.locationId) {
    return AppState.globalUsers
  }
  if (
    usersLoadedForLocation === AppState.locationId &&
    Array.isArray((AppState.globalUsers as any)?.users) &&
    !fetchData
  ) {
    return AppState.globalUsers
  }

  const requestKey = AppState.locationId
  const inFlightRequest = usersInLocationRequests.get(requestKey)
  if (inFlightRequest) {
    return inFlightRequest
  }

  AppState.loadingAssets.users = true
  const request = UserService.getAll(AppState.locationId).then(({ data }) => {
    if (AppState.locationId === requestKey) {
      setGlobalUsers(data)
      usersLoadedForLocation = requestKey
    }
    return data
  })
  usersInLocationRequests.set(requestKey, request)

  try {
    return await request
  } finally {
    if (usersInLocationRequests.get(requestKey) === request) {
      usersInLocationRequests.delete(requestKey)
    }
    AppState.loadingAssets.users = false
  }
}

export const getTeamsInLocation = async (fetchData = false) => {
  if (!AppState.locationId) {
    return AppState.globalTeams
  }
  if (
    teamsLoadedForLocation === AppState.locationId &&
    Array.isArray((AppState.globalTeams as any)?.teams) &&
    !fetchData
  ) {
    return AppState.globalTeams
  }

  const requestKey = AppState.locationId
  const inFlightRequest = teamsInLocationRequests.get(requestKey)
  if (inFlightRequest) {
    return inFlightRequest
  }

  AppState.loadingAssets.groups = true
  const request = CalendarGroupService.getAll(AppState.locationId).then(
    ({ data }) => {
      if (AppState.locationId === requestKey) {
        setGlobalTeams({ teams: data.groups })
        teamsLoadedForLocation = requestKey
      }
      return data
    }
  )
  teamsInLocationRequests.set(requestKey, request)

  try {
    return await request
  } finally {
    if (teamsInLocationRequests.get(requestKey) === request) {
      teamsInLocationRequests.delete(requestKey)
    }
    AppState.loadingAssets.groups = false
  }
}

export const getRoomsInLocation = async (fetchData = false) => {
  if (!AppState.locationId) {
    return AppState.globalRooms
  }
  if (AppState.globalRooms && !fetchData) {
    return AppState.globalRooms
  }
  AppState.loadingAssets.rooms = true
  const { data } = await CalendarResourceService.find(AppState.locationId, CALENDAR_RESOURCE_TYPES.ROOMS, 200)
  setGlobalRooms(data)
  AppState.loadingAssets.rooms = false
  return data
}

export const getEquipmentsInLocation = async (fetchData = false) => {
  if (!AppState.locationId) {
    return AppState.globalEquipments
  }
  if (AppState.globalEquipments && !fetchData) {
    return AppState.globalEquipments
  }
  AppState.loadingAssets.equipments = true
  const { data } = await CalendarResourceService.find(AppState.locationId, CALENDAR_RESOURCE_TYPES.EQUIPMENTS, 200)
  setGlobalEquipments(data)
  AppState.loadingAssets.equipments = false
  return data
}

export const getFormsInLocation = async () => {
  if (AppState.forms.length) {
    return AppState.forms
  }

  const batchSize = 50
  const {
    data: { forms, total },
  } = await formService.getAll(AppState.locationId, 0)

  const totalBatches = Math.ceil((total - batchSize) / batchSize)

  const fetchedForms = await Promise.all(
    Array.from({ length: totalBatches }, (_, i) => formService.getAll(AppState.locationId, (i + 1) * batchSize))
  )

  fetchedForms.forEach(({ data: { forms: nextForms } }) => {
    if (nextForms && nextForms.length > 0) {
      forms.push(...nextForms)
    }
  })

  AppState.forms = forms
}

export const getExternalCalendars = async () => {
  if (Object.keys(AppState.externalCalendars).length > 0) {
    return
  }
  const {
    data: { calendars },
  } = await CalendarService.getExternalGoogleCalendars(AppState.locationId)
  AppState.externalCalendars = calendars
}

export const getEditingCalendar = async (calendarId: string) => {
  AppState.loadingAssets.editingCalendar = true
  const { data } = await CalendarService.get(calendarId)
  setEditingCalendar(new Calendar(data.calendar))
  AppState.loadingAssets.editingCalendar = false
  return data
}

export const getCustomValues = async () => {
  if (AppState.locationId) {
    if (AppState.isCustomValuesLoaded) {
      return AppState.customValues
    }
    const { data } = await CustomValueService.getByLocationId(AppState.locationId)
    AppState.customValues = data.customValues
    AppState.isCustomValuesLoaded = true
  }
  return AppState.customValues as any[]
}

// Flag to track if nested custom values have been loaded for current location
let nestedCustomValuesLoadedForLocation: string | null = null

export const getNestedCustomValues = async () => {
  if (AppState.locationId) {
    // Return cached values if already loaded for this location
    if (nestedCustomValuesLoadedForLocation === AppState.locationId) {
      return AppState.nestedCustomValues
    }
    try {
      const { data } = await CustomValueService.getCustomValues(AppState.locationId)
      AppState.nestedCustomValues = data?.['custom-values'] || []
      nestedCustomValuesLoadedForLocation = AppState.locationId
    } catch (error) {
      console.error('error: fetch custom values ', error)
      // Don't set the flag on error so it can retry
    }
  }
  return AppState.nestedCustomValues as any[]
}

export const getEditingServiceMenu = async (serviceMenuId: string) => {
  if (serviceMenuId) {
    AppState.loadingAssets.editingServiceMenu = true
    const { data } = await CalendarServiceMenuService.get(serviceMenuId)
    setEditingServiceMenu(new ServiceMenu(data))
    AppState.loadingAssets.editingServiceMenu = false
    return data
  }
}

export const getCalendarsInLocation = async (fetchData = false, showThirdPartyCal = false) => {
  if (!AppState.locationId) {
    return AppState.globalCalendars
  }

  const requestKey = `${AppState.locationId}:${showThirdPartyCal}`
  if (
    calendarsLoadedForRequestKey === requestKey &&
    Array.isArray((AppState.globalCalendars as any)?.calendars) &&
    !fetchData
  ) {
    return AppState.globalCalendars
  }

  const inFlightRequest = calendarsInLocationRequests.get(requestKey)
  if (inFlightRequest) {
    return inFlightRequest
  }

  AppState.loadingAssets.calendars = true
  const request = CalendarService.getAll(
    AppState.locationId,
    showThirdPartyCal,
    true
  ).then(({ data }) => {
    if (requestKey === `${AppState.locationId}:${showThirdPartyCal}`) {
      setGlobalCalendars(data)
      calendarsLoadedForRequestKey = requestKey
    }
    return data
  })
  calendarsInLocationRequests.set(requestKey, request)

  try {
    return await request
  } finally {
    if (calendarsInLocationRequests.get(requestKey) === request) {
      calendarsInLocationRequests.delete(requestKey)
    }
    AppState.loadingAssets.calendars = false
  }
}

export const getCalendarsOfUser = async (userId: string) => {
  const data = await getCalendarsInLocation(true)
  return (
    data?.calendars?.filter(
      calendar =>
        calendar?.calendarType !== CalendarType.EVENT &&
        calendar?.teamMembers &&
        calendar?.teamMembers?.find(teamMember => teamMember.userId === userId)
    ) || []
  )
}

export const clearGlobalCache = () => {
  AppState.globalTeams = null as any
  AppState.globalCalendars = null as any
  AppState.hasServiceCalendars = false
  AppState.globalUsers = null as any
  AppState.globalRooms = null as any
  AppState.globalEquipments = null as any
  AppState.globalServiceMenus = null
  usersLoadedForLocation = null
  teamsLoadedForLocation = null
  calendarsLoadedForRequestKey = null
  invalidateUserDetailsCache()
  invalidateCalendarUserSettingsCache()
}

export const fetchRequiredData = async (loadInBackground = false) => {
  if (!loadInBackground) {
    setIsLoading(true)
  }
  await Promise.all([
    fetchCalendarLocationSettings(),
    getTeamsInLocation(),
    getCalendarsInLocation(),
    getUsersInLocation(),
    AppState.isUserPartOfLocation ? getCalendarOnboardingStatus() : Promise.resolve(),
  ])
  if (!loadInBackground) {
    setIsLoading(false)
  }
}

export const fetchUserDetails = async (userId?: string, loadInBackground = false, forceRefresh = false) => {
  if (!userId) {
    return
  }

  const cachedUserDetails = userDetailsCache.get(userId)
  if (!forceRefresh && cachedUserDetails && Date.now() - cachedUserDetails.fetchedAt < USER_DETAILS_CACHE_TTL_MS) {
    setLoggedInUserDetail(cachedUserDetails.user)
    return cachedUserDetails.user
  }

  const inFlightRequest = userDetailsRequests.get(userId)
  if (!forceRefresh && inFlightRequest) {
    return inFlightRequest
  }

  if (!loadInBackground) {
    setIsLoading(true)
  }

  const requestId = ++userDetailsRequestId
  latestUserDetailsRequestIds.set(userId, requestId)

  const request = UserService.getById(userId).then(({ data }) => {
    const user = new User(data)
    const isLatestRequest = latestUserDetailsRequestIds.get(userId) === requestId

    if (isLatestRequest) {
      userDetailsCache.set(userId, {
        fetchedAt: Date.now(),
        user,
      })
      setLoggedInUserDetail(user)
    }

    return user
  })

  userDetailsRequests.set(userId, request)

  try {
    return await request
  } finally {
    if (userDetailsRequests.get(userId) === request) {
      userDetailsRequests.delete(userId)
    }
    if (!loadInBackground) {
      setIsLoading(false)
    }
  }
}

export const fetchLocationDetails = async (locationId?: string) => {
  if (!locationId) {
    return
  }

  const { data } = await LocationService.getById(locationId)
  setCurrentLocationDetail(new Location(data.location))
}

export const ensureCurrentLocationDetails = async (locationId = AppState.locationId) => {
  if (!locationId) {
    return
  }

  if (AppState.currentLocationDetail?.id === locationId) {
    return
  }

  try {
    await fetchLocationDetails(locationId)
  } catch {
    return
  }
}

export const fetchCalendarLocationSettings = async () => {
  if (!AppState.locationId) {
    return
  }

  const { data } = await CalendarLocationSettingsService.getLocationSettings(AppState.locationId)
  setCalendarLocationDetail(new CalendarLocationSettings(data.locationSettings))
}

export const fetchConnections = async (locationId?: string) => {
  if (!locationId || AppState.isCalendarsConnectionLoaded) {
    return
  }

  const response = await ConnectionService.getConnections(locationId)
  if (!response) {
    return
  }
  const { data } = response
  setConnections(data)
  AppState.isCalendarsConnectionLoaded = true
}

export interface ISelectOption {
  label: string | number
  value: string | number | boolean
  disabled?: boolean
  type?: string
}

export const getMerchantInLocation = async () => {
  if (AppState.isMerchantsLoaded || !AppState.locationId) {
    return
  }
  const { data } = await CalendarService.fetchMerchant(AppState.locationId)
  if (data?.length) {
    AppState.merchant = data[0]
  }
  AppState.isMerchantsLoaded = true
  return AppState.merchant
}

export const isServiceCalendarEnabled = () => {
  return AppState?.calendarLocationDetails?.featureFlags?.isServiceMenuEnabled
}

export const isRoomEnabled = () => {
  return AppState?.calendarLocationDetails?.featureFlags?.isRoomEnabled
}

export const isEquipmentEnabled = () => {
  return AppState?.calendarLocationDetails?.featureFlags?.isEquipmentEnabled
}

export const isArchivedServicesTabVisible = () => {
  const hasExistingServiceCalendars =
    AppState.hasServiceCalendars ||
    hasServiceCalendars(AppState.globalCalendars?.calendars)
  const isServiceMenuOn = Boolean(isServiceCalendarEnabled())
  const isRoomsOn = Boolean(isRoomEnabled())
  const isEquipmentsOn = Boolean(isEquipmentEnabled())

  return (
    hasExistingServiceCalendars ||
    isServiceMenuOn ||
    isRoomsOn ||
    isEquipmentsOn
  )
}
export function setClonedEmailTemplateId(payload: { clonedTemplateId: string }) {
  AppState.clonedEmailTemplateId = payload.clonedTemplateId
}

export const getServiceMenusInLocation = async (fetchData = false) => {
  if (!AppState.locationId) {
    return
  }
  if (AppState.globalServiceMenus && !fetchData) {
    return
  }
  AppState.loadingAssets.serviceMenus = true
  const { data } = await CalendarServiceMenuService.getAll(AppState.locationId)
  setGlobalServiceMenus({ serviceMenus: data.serviceMenus })
  AppState.loadingAssets.serviceMenus = false
  return data
}

export const getLoggedInUserTimezone = () => {
  const locationWiseTimezone = AppState.loggedInUserDetail?.locationWiseTimezone || {}
  return locationWiseTimezone[AppState.locationId] || AppState.locationTimezone
}

export const getTargetTimezone = () => {
  return AppState.userPreferenceTimezone === 'business_timezone' ? AppState.locationTimezone : AppState.userPreferenceTimezone
}

export const updateCalendarStore = () => {
  AppState.parentApp?.then((parent: any) => {
    parent.emit('update-calendars')
  })
}

export async function getEmailTemplates(reload = false) {
  if (AppState.emailTemplates && !reload) {
    return AppState.emailTemplates
  }
  AppState.emailTemplates = await TemplateService.getEmailTemplates()
  return AppState.emailTemplates
}

export async function getSmsTemplates(reload = false) {
  if (AppState.smsTemplates && !reload) {
    return AppState.smsTemplates
  }
  AppState.smsTemplates = await TemplateService.getSMSTemplates()
  return AppState.smsTemplates
}

export async function getWhatsAppDefaultTemplates(reload = false) {
  if (AppState.whatsappDefaultTemplates && !reload) {
    return AppState.whatsappDefaultTemplates
  }

  try {
    const response = await WhatsappTemplateService.getDefaultTemplates(AppState.locationId)
    AppState.whatsappDefaultTemplates = response.data.templates || []
    return AppState.whatsappDefaultTemplates
  } catch (error) {
    console.error('Error fetching WhatsApp default templates:', error)
    AppState.whatsappDefaultTemplates = []
    return AppState.whatsappDefaultTemplates
  }
}

export async function getWhatsAppTemplates(reload = false) {
  if (AppState.whatsappTemplates && !reload) {
    return AppState.whatsappTemplates
  }

  AppState.whatsappTemplates = await TemplateService.getWhatsAppTemplates()
  return AppState.whatsappTemplates
}

export async function fetchCustomFields(reload = false): Promise<any[]> {
  if (AppState.customFields && !reload) {
    return AppState.customFields
  }
  const allFields = await CustomFieldService.getAllFields()
  AppState.customFields = allFields?.filter(field => {
    return field.dataType !== 'STANDARD_FIELD'
  })

  //setting picklistOptionsImage to picklistImageOptions, to maintain the consistency of contacts and opportunities
  AppState.customFields = AppState.customFields?.map(item => {
    if (item.picklistOptions === undefined && item.picklistOptionsImage) {
      item.picklistImageOptions = item.picklistOptionsImage
    }
    return item
  })

  AppState.standardFields = allFields?.filter(field => {
    return field.dataType === 'STANDARD_FIELD'
  })

  // sort custom fields
  if (AppState.customFields) {
    AppState.customFields = sortOptions(AppState.customFields, 'name')
  }

  return AppState.customFields || []
}

export async function fetchOpportunityCustomFields(reload = false): Promise<any> {
  if (AppState.opportunityCustomFields && !reload) {
    return AppState.opportunityCustomFields
  }
  AppState.isLoading = true
  const resp = (await CustomFieldService.getCustomFields(AppState.locationId)) as any

  AppState.opportunityCustomFields = resp?.customFields
  AppState.isLoading = false

  // sort custom fields
  if (AppState.opportunityCustomFields) {
    AppState.opportunityCustomFields = sortOptions(AppState.opportunityCustomFields, 'name')
  }

  return AppState.opportunityCustomFields || []
}

export const calendarOptions = computed(() => {
  const allCalendars = (AppState.globalCalendars as any)?.calendars
  return allCalendars?.map(calendar => ({
    label: calendar.name,
    value: calendar.id,
  }))
})

export const updateCalendarOnboardingStatus = async (body: any) => {
  await CalendarUserSettings.updateUserSettingsFeatures(body)
  invalidateCalendarUserSettingsCache(body?.locationId, body?.userId)
}

export const getCalendarUserSettings = async (
  locationId: string,
  userId: string,
  forceRefresh = false
) => {
  const requestKey = `${locationId}:${userId}`
  const cachedUserSettings = calendarUserSettingsCache.get(requestKey)
  if (
    !forceRefresh &&
    cachedUserSettings &&
    Date.now() - cachedUserSettings.fetchedAt <
      CALENDAR_USER_SETTINGS_CACHE_TTL_MS
  ) {
    return cachedUserSettings.response
  }

  const inFlightRequest = calendarUserSettingsRequests.get(requestKey)
  if (inFlightRequest) {
    return inFlightRequest
  }

  const request = CalendarUserSettings.getUserSettings(
    locationId,
    userId
  ).then(response => {
    calendarUserSettingsCache.set(requestKey, {
      fetchedAt: Date.now(),
      response,
    })
    return response
  })
  calendarUserSettingsRequests.set(requestKey, request)

  try {
    return await request
  } finally {
    if (calendarUserSettingsRequests.get(requestKey) === request) {
      calendarUserSettingsRequests.delete(requestKey)
    }
  }
}

export const getCalendarOnboardingStatus = async (forceRefresh = false) => {
  try {
    if (!AppState.locationId || !AppState.user?.id || !AppState.isUserPartOfLocation) {
      return
    }
    AppState.isOnboardingLoading = true
    const { data } = await getCalendarUserSettings(
      AppState.locationId,
      AppState.user?.id as string,
      forceRefresh
    )
    if (data) {
      AppState.calendarsOnboarding = data?.calendarOnboarding || []
      AppState.onboardingError = false
    }
    AppState.isOnboardingLoading = false
  } catch (error) {
    AppState.calendarsOnboarding = []
    AppState.onboardingError = true
    AppState.isOnboardingLoading = false
  }
}

export function resetLocationChanged() {
  AppState.hasLocationChanged = false
}
export const isPermissionsEnabled = (scope: CalendarScopes) => {
  const scopes = AppState.loggedInUserDetail?.scopes || []
  return scopes?.includes(scope)
}

const userDataLoaded = computed(() => !!AppState.loggedInUserDetail)

const isLegacyAdmin = computed(() => {
  const t = AppState.loggedInUserDetail?.type
  const r = AppState.loggedInUserDetail?.role
  return t === 'agency' || (t === 'account' && r === 'admin')
})

export const canView = computed(() => {
  return isPermissionsEnabled(CalendarScopes.READ_CALENDARS)
})

export const isCreateCalendarDisabled = computed(() => {
  return !isPermissionsEnabled(CalendarScopes.EDIT_CALENDARS)
})

// Service menus are "associated components" — same permission as resources.
export const isCreateServiceMenuDisabled = computed(() => {
  return !isPermissionsEnabled(CalendarScopes.EDIT_CALENDARS)
})

export const isCreateGroupDisabled = computed(() => {
  return !isPermissionsEnabled(CalendarScopes.EDIT_CALENDAR_GROUPS)
})

// Rooms and equipment are "associated components" — gated by resources.write.
export const isCreateRoomDisabled = computed(() => {
  return !isPermissionsEnabled(CalendarScopes.EDIT_CALENDAR_RESOURCES)
})

export const isCreateEquipmentDisabled = computed(() => {
  return !isPermissionsEnabled(CalendarScopes.EDIT_CALENDAR_RESOURCES)
})

export const isRentalsEnabled = computed(() => {
  return AppState.calendarSubAccountConfig?.modules?.includes('rentals') || false
})

export const recurringAppointmentPaymentOptions = (t: any) => {
  return [
    {
      value: RecurringAppointmentsPaymentType.FirstAppointmentOnly,
      label: t('calendar_advanced.forms_payment.first_appointment_only'),
    },
    {
      value: RecurringAppointmentsPaymentType.AllAppointments,
      label: t('calendar_advanced.forms_payment.all_appointments'),
    },
  ]
}

export const updateTeamsStore = () => {
  AppState.parentApp?.then((parent: any) => {
    parent.emit('update-teams')
  })
}

export const updateUserPreferencesStore = () => {
  AppState.parentApp?.then((parent: any) => {
    parent.emit('update-user-timezone-preference')
  })
}

export const showTableButton = computed(() => {
  return AppState.isServicesEnabled && AppState.selectedSettingsTab === 'archived'
})

// Computed properties for frequently used custom object labels
export const contactLabelSingular = computed(() => {
  return AppState.customObjectLabels?.contact?.singular || 'contact'
})

export const contactLabelPlural = computed(() => {
  return AppState.customObjectLabels?.contact?.plural || 'contacts'
})

export const contactLabelSingularLower = computed(() => {
  return contactLabelSingular.value.toLowerCase()
})

export const contactLabelPluralLower = computed(() => {
  return contactLabelPlural.value.toLowerCase()
})

export default AppState
